import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { router } from '@/plugins/router';
import API from '@/utils/api';
import handleRequest from '@/utils/helpers/handleRequest';

const usePartnerAuthStore = defineStore('partnerAuthStore', () => {
  const partner = ref<Partner | null>(null);
  const authLoading = ref(false);
  const meChecked = ref(false);

  const isAuth = computed(() => !!partner.value?.id);

  const setPartner = (data: Partner) => {
    partner.value = data;
  };

  const reset = () => {
    partner.value = null;
    meChecked.value = false;
    authLoading.value = false;
  };

  const getMe = async () => {
    authLoading.value = true;
    const { isError, data, response } = await handleRequest(
      API.get('/partner/me'),
    );
    if (!isError) {
      setPartner(data);
      meChecked.value = true;
    } else if (Number(response?.response?.status) === 401) {
      // A definitive "not logged in" answer; anything else (network error,
      // 5xx) is transient, so leave meChecked false to allow a retry on the
      // next navigation instead of stranding an actually-authenticated
      // partner on the login page.
      partner.value = null;
      meChecked.value = true;
    }
    authLoading.value = false;
  };

  const onLogin = async (reqData: { email: string; password: string }) => {
    const { data, isError } = await handleRequest(async () => {
      await API.csrf();
      return API.post('/partner/login', reqData);
    });
    if (!isError) {
      setPartner(data);
      meChecked.value = true;
      await router.replace({ name: 'partner.dashboard' });
    }
  };

  const onLogout = async () => {
    const { isError } = await handleRequest(API.post('/partner/logout'));
    if (!isError) {
      reset();
      await router.replace({ name: 'partner.login' });
    }
  };

  return {
    partner,
    authLoading,
    meChecked,
    isAuth,
    getMe,
    setPartner,
    onLogin,
    onLogout,
    reset,
  };
});

export default usePartnerAuthStore;
